import { type Timestamp, timestampFromDate } from '@bufbuild/protobuf/wkt';
import { add } from '@qdrant-cloud-ui/date/add';
import ipaddr from 'ipaddr.js';
import { z } from 'zod';

export const CLUSTER_NAME_HELPER_TXT = 'Can only contain alphanumeric characters, hyphens and underscores.';

/**
 * `z.preprocess` with the declared input pinned to the wrapped schema's output, replacing the
 * empty string a cleared field yields with `empty`, which defaults to `undefined`. Pass `null`
 * when the schema's own empty is null.
 *
 * Zod types a bare `z.preprocess` as taking `unknown`, which since @hookform/resolvers v5 leaks
 * into the resolver as `Resolver<unknown-shaped input, ctx, output>` and forces every form onto
 * the three-generic `useForm<Input, Context, Output>`. Pinning the input keeps `z.input` equal to
 * `z.output`, so the forms stay on `useForm<FormValues>` — the same single shape they have always
 * declared. The runtime is untouched: the cast is only how the widget's raw value is described.
 */
export const preprocessEmptyStringTo = <TSchema extends z.ZodType>(
  schema: TSchema,
  empty?: Extract<z.input<TSchema>, null | undefined>,
) => z.preprocess((value: z.output<TSchema>) => ((value as unknown) === '' ? empty : value), schema);

export const REG_EXPS: Record<string, { regExp: RegExp; errorMessage: string }> = {
  alpha_2: {
    regExp: /^[a-z]{2}$/i,
    errorMessage: 'Invalid country code',
  },
  chartRepositoryUrl: {
    regExp: /^oci:\/\/[a-zA-Z0-9.\-/]+$/i,
    errorMessage: 'Chart repository URL must start with oci://',
  },
  clusterName: {
    regExp: /^[a-z0-9_-]+$/i,
    errorMessage: CLUSTER_NAME_HELPER_TXT,
  },
  containerRegistryUrl: {
    regExp: /^[a-zA-Z0-9.\-/]+$/i,
    errorMessage: 'Container registry URL must not contain a protocol',
  },
  kubernetesLabelKey: {
    // Matches: [OPTIONAL_PREFIX/](NAME)
    // Prefix: DNS subdomain (253 chars max), Name: Qualified name (63 chars max)
    regExp:
      // eslint-disable-next-line @stylistic/js/max-len
      /^(?:(?=.{1,253}\/)([A-Za-z0-9](?:[-A-Za-z0-9]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[-A-Za-z0-9]*[A-Za-z0-9])?)*)\/)?(?=.{1,63}$)([A-Za-z0-9](?:[-A-Za-z0-9_.]*[A-Za-z0-9])?)$/,
    errorMessage:
      'Must be a valid label key. If a prefix is used, it must be a DNS subdomain (max 253 chars). ' +
      'The name part must be alphanumeric (max 63 chars) and can include dots, hyphens, or underscores.',
  },
  kubernetesNamespace: {
    regExp: /^(?!.*--)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/,
    errorMessage:
      'Can only contain lower case letters, numbers and hyphens (not consecutive),' +
      ' and must start and end with a letter or number',
  },
  kubernetesQualifiedName: {
    regExp: /^(?=.{1,63}$)([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/,
    errorMessage:
      'Can only contain letters, numbers, dots, hyphens and underscores' +
      'and must start and end with a letter or a number',
  },
  kubernetesResourceName: {
    // See https://kubernetes.io/docs/concepts/overview/working-with-objects/names/
    regExp: /^(?!.*(\.\.|--|(\.-)|(-\.)))[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?$/,
    errorMessage:
      'Can only contain lower case letters, numbers, hyphens and dots (not consecutive),' +
      ' and must start and end with a letter or number',
  },
  kubernetesSecret: {
    regExp: /^[A-Za-z0-9_.-]{1,64}$/i,
    errorMessage: 'Can only contain letters, numbers, dots, hyphens and underscores',
  },
  name: {
    // Matches everything EXCEPT C0 and C1 control codes (0-31, 127, 128-159)
    regExp: /^([^\p{Cc}])*$/gu,
    errorMessage: 'String contains invalid character(s)',
  },
  postcode: {
    regExp: /^[a-z0-9][a-z0-9\- ]{0,10}[a-z0-9]$/i,
    errorMessage: 'Invalid postal code',
  },
};

const MAX_KUBERNETES_NAME_LENGTH = 63;
export const MAX_URL_LENGTH = 2048;
const getExceededMaxLengthErrorMsg = (max: number) => `Must be at most ${max} characters`;
const getBelowMinLengthErrorMsg = (min: number) => `Must be at least ${min} character${min > 1 ? 's' : ''}`;

/**
 * A right-most label made entirely of digits passes `z.hostname()` but is not a valid
 * protovalidate hostname — it is ambiguous with an IP address — so the API gateway rejects it. The
 * `hostname` validator below excludes it too, which makes it exactly match protovalidate's
 * `string.hostname` rule (`IsHostname` in `buf.build/go/protovalidate`), the rule the gateway
 * enforces on `clusterDomain`.
 *
 * Ref: https://github.com/bufbuild/protovalidate-go/blob/v1.4.0/internal/rules/rules.go
 */
const hasAllDigitTopLabel = (value: string): boolean => {
  const labels = (value.endsWith('.') ? value.slice(0, -1) : value).split('.');
  return /^\d+$/.test(labels[labels.length - 1]);
};

const _validators = {
  // ISO 3166-1 alpha-2
  alpha2: z
    .string()
    .trim()
    .regex(REG_EXPS.alpha_2.regExp, REG_EXPS.alpha_2.errorMessage)
    .transform((value) => value.toUpperCase()),
  chartRepositoryUrl: z
    .string()
    .min(1)
    .max(MAX_URL_LENGTH, getExceededMaxLengthErrorMsg(MAX_URL_LENGTH))
    .regex(REG_EXPS.chartRepositoryUrl.regExp, REG_EXPS.chartRepositoryUrl.errorMessage),
  clusterName: z
    .string()
    .trim()
    .min(4, getBelowMinLengthErrorMsg(4))
    .max(64, getExceededMaxLengthErrorMsg(64))
    .pipe(z.string().trim().regex(REG_EXPS.clusterName.regExp, REG_EXPS.clusterName.errorMessage)),
  containerRegistryUrl: z
    .string()
    .min(1)
    .max(MAX_URL_LENGTH, getExceededMaxLengthErrorMsg(MAX_URL_LENGTH))
    .regex(REG_EXPS.containerRegistryUrl.regExp, REG_EXPS.containerRegistryUrl.errorMessage),
  domain: z.string().trim().min(1, getBelowMinLengthErrorMsg(1)).max(253, getExceededMaxLengthErrorMsg(253)),
  hostname: z
    .hostname({ error: 'Must be a valid hostname, e.g. cluster.local' })
    .refine((value) => !hasAllDigitTopLabel(value), {
      error: 'The right-most label of a hostname cannot be all digits',
    }),
  ipv4CidrWithCanonicalPrefix: z
    .string()
    .cidrv4('IPv4 CIDR range expected')
    .superRefine((val, ctx) => {
      try {
        const [addr, mask] = ipaddr.parseCIDR(val);
        if (addr.kind() === 'ipv4') {
          const range = ipaddr.IPv4.networkAddressFromCIDR(val);
          const canonical = `${range.toString()}/${mask}`;
          if (addr.toString() !== range.toString()) {
            ctx.addIssue({
              code: 'custom',
              message: `This CIDR is not in canonical form. Did you mean ${canonical}?`,
              fatal: true,
            });
          }
        }
      } catch (err) {
        const detail = err instanceof Error ? `: ${err.message}` : '';
        ctx.addIssue({
          code: 'custom',
          message: `Unexpected error while processing IPv4 CIDR${detail}`,
          fatal: true,
        });
      }
    }),
  kubernetesLabelKey: z
    .string()
    .min(1, getBelowMinLengthErrorMsg(1))
    // We don't use .max(63) here because the total length (prefix + name) can be ~317 chars.
    // The regex lookaheads handle the specific 253/63 limits.
    .regex(REG_EXPS.kubernetesLabelKey.regExp, REG_EXPS.kubernetesLabelKey.errorMessage),
  kubernetesNamespace: z
    .string()
    .min(1, getBelowMinLengthErrorMsg(1))
    .max(MAX_KUBERNETES_NAME_LENGTH, getExceededMaxLengthErrorMsg(MAX_KUBERNETES_NAME_LENGTH))
    .regex(REG_EXPS.kubernetesNamespace.regExp, REG_EXPS.kubernetesNamespace.errorMessage),
  kubernetesQualifiedName: z
    .string()
    .min(1, getBelowMinLengthErrorMsg(1))
    .max(MAX_KUBERNETES_NAME_LENGTH, getExceededMaxLengthErrorMsg(MAX_KUBERNETES_NAME_LENGTH))
    .regex(REG_EXPS.kubernetesQualifiedName.regExp, REG_EXPS.kubernetesQualifiedName.errorMessage),
  kubernetesResourceName: z
    .string()
    .min(1, getBelowMinLengthErrorMsg(1))
    .max(MAX_KUBERNETES_NAME_LENGTH, getExceededMaxLengthErrorMsg(MAX_KUBERNETES_NAME_LENGTH))
    .regex(REG_EXPS.kubernetesResourceName.regExp, REG_EXPS.kubernetesResourceName.errorMessage),
  kubernetesSecret: z
    .string()
    .min(1, getBelowMinLengthErrorMsg(1))
    .max(MAX_KUBERNETES_NAME_LENGTH, getExceededMaxLengthErrorMsg(MAX_KUBERNETES_NAME_LENGTH))
    .regex(REG_EXPS.kubernetesSecret.regExp, REG_EXPS.kubernetesSecret.errorMessage),
  // 4-256 characters, letters, numbers, marks, punctuation, spaces, hyphens
  name: z
    .string()
    .trim()
    .min(4, getBelowMinLengthErrorMsg(4))
    .max(256, getExceededMaxLengthErrorMsg(256))
    .regex(REG_EXPS.name.regExp, REG_EXPS.name.errorMessage),
  // 2-12 characters, letters, numbers, spaces, hyphens
  postcode: z.string().regex(REG_EXPS.postcode.regExp, REG_EXPS.postcode.errorMessage),
  // 1-63 characters, letters, numbers, marks, punctuation, spaces, hyphens
  readableName: z
    .string()
    .trim()
    .min(1)
    .max(MAX_KUBERNETES_NAME_LENGTH, getExceededMaxLengthErrorMsg(MAX_KUBERNETES_NAME_LENGTH))
    .regex(REG_EXPS.name.regExp, REG_EXPS.name.errorMessage),
  uri: z.url('Invalid URL format').max(MAX_URL_LENGTH, getExceededMaxLengthErrorMsg(MAX_URL_LENGTH)),
};

/**
 * Max API key expiry was arbitrarily chosen to put a size limit on the input. And 10 years should
 * suffice.
 */
export const MAX_API_KEY_EXPIRY_DAYS = 3650; // ~ 10 YEARS

/**
 * Optional "days until expiration" input, shared by the cluster and serverless space API key forms.
 * When defining an optional number with a minimum, it has to be done through `refine` with a string
 * as an entry point (and then parsed inside), to prevent early validation failures.
 * At the same time this has to be combined with a `Controller`.
 */
export const expirationDaysValidator = z
  .string()
  .optional()
  .refine(
    (value) => {
      if (value == null || value === '') {
        return true;
      }
      const parsedInt = parseInt(value);
      if (isNaN(parsedInt)) {
        return false;
      }

      // To prevent values such as '1.1', '1e3' or '1abcde#' from being accepted, as parse int will return a number
      if (parsedInt.toString() !== value) {
        return false;
      }

      if (parsedInt < 1 || parsedInt > MAX_API_KEY_EXPIRY_DAYS) {
        return false;
      }
      return true;
    },
    { error: `Value must be an integer between 1 and ${MAX_API_KEY_EXPIRY_DAYS} when defined` },
  );

/** An empty expiration means "never expires", which both API key APIs model as an unset field. */
export const getApiKeyExpiresAt = (expiration: string | undefined): Timestamp | undefined =>
  expiration ? timestampFromDate(add(new Date(), { days: Number(expiration) })) : undefined;

/**
 * The validator, plus the empty string a cleared field yields, plus `undefined`.
 *
 * The empty string is short-circuited before the validator instead of sitting in a
 * `z.union([validator, z.string().length(0)])`: zod 4 answers a failed union with whichever branch
 * raised the fewest issues, so any input that broke two of the validator's rules at once (a URL
 * both too long and malformed) surfaced `z.string().length(0)`'s default message instead of ours.
 *
 * Not `z.ZodString`: the top-level formats (`z.url()`, `z.email()`, …) are `ZodStringFormat`,
 * a sibling class, so the parameter is typed by what it produces instead.
 */
const getOptionalStringValidator = (validator: z.ZodType<string, string>) =>
  z
    .string()
    .optional()
    .transform((value, ctx) => {
      if (value === undefined || value === '') {
        return value;
      }
      const result = validator.safeParse(value);
      if (result.success) {
        return result.data;
      }
      for (const issue of result.error.issues) {
        // Spread, not the issue itself: `addIssue` asks for an index signature an interface lacks.
        ctx.addIssue({ ...issue });
      }
      return z.NEVER;
    })
    // A transform's output is never optional, so without this the key turns required in `z.output`
    // and every resolver stops matching the single `useForm<FormValues>` shape.
    .optional();

// Add optional version of some validators
export const validators = Object.assign({}, _validators, {
  ipv4CidrWithCanonicalPrefix: _validators.ipv4CidrWithCanonicalPrefix,
  optionalChartRepositoryUrl: getOptionalStringValidator(_validators.chartRepositoryUrl),
  optionalContainerRegistryUrl: getOptionalStringValidator(_validators.containerRegistryUrl),
  optionalHostname: getOptionalStringValidator(_validators.hostname),
  optionalKubernetesLabelKey: getOptionalStringValidator(_validators.kubernetesLabelKey),
  optionalKubernetesQualifiedName: getOptionalStringValidator(_validators.kubernetesQualifiedName),
  optionalKubernetesResourceName: getOptionalStringValidator(_validators.kubernetesResourceName),
  optionalKubernetesSecret: getOptionalStringValidator(_validators.kubernetesSecret),
  optionalName: getOptionalStringValidator(_validators.name).transform((e) => (e === '' ? undefined : e)),
  optionalUri: getOptionalStringValidator(_validators.uri),
});
